import { defineStore } from 'pinia';
import router from '@/router';
import { useFilterStore } from '@/stores';
import {
    AuthServiceLoginResult,
    AuthState,
    CompanyModel,
    Permission,
    UserIntegrationType,
    UserModel,
} from '@/types';
import { toast } from '@/services';
import { inactivity } from '@/plugins/inactivity';
import AnalyticsService from '../../services/analytics/AnalyticsService';

export const useAuthStore = defineStore('auth', {
    state: (): AuthState => ({
        user: undefined,
        company: undefined,
    }),

    getters: {
        userModel: (state): UserModel | undefined => state.user,
        userCompany: (state): CompanyModel | undefined => state.company,
        userLevels: (state) => state.user?.levels ?? [],
        userRoles: (state) => state.user?.roles ?? [],
        userPermissions: (state): Permission[] => {
            const permissions: Permission[] = [];
            const roles = state.user?.roles || [];

            for (let i = 0; i < roles.length; i++) {
                const rolePermissions = roles[i].permissions || [];
                for (let ii = 0; ii < rolePermissions.length; ii++) {
                    const rolePermission = rolePermissions[ii].id as Permission;
                    if (
                        Object.values(Permission).includes(rolePermission) &&
                        !permissions.includes(rolePermission)
                    )
                        permissions.push(rolePermission);
                }
            }

            return permissions;
        },
        userNeedsUpdatePassword: (state) => {
            if (state?.user !== undefined) {
                return !state?.user.lastLogin || state?.user.isPasswordExpired;
            }

            return false;
        },
        chatBotIntegration: (state) => {
            if (state.user !== undefined && state.user?.integrations !== undefined)
                return state.user?.integrations.find((integration) => {
                    return (
                        integration.integration === UserIntegrationType.CHAT_BOT &&
                        integration.levelId === state.user?.currentLevel?.id
                    );
                });
        },
    },

    actions: {
        login(loginResult: AuthServiceLoginResult) {
            const filterStore = useFilterStore();

            const { user, level, company } = loginResult;

            this.user = user as UserModel;
            this.company = company as CompanyModel;

            filterStore.setLevel(level);

            if (loginResult.jwt) {
                localStorage.setItem('token', loginResult.jwt);
                AnalyticsService.trackingLogin().then();
            }

            if (loginResult.jwtTemp) {
                localStorage.setItem('tokenTemp', loginResult.jwtTemp);
            }

            const companyTimeout = this.company?.logoffByInactivity ?? 0;
            if (companyTimeout > 0) {
                const timeoutMs = Math.max(1, Math.floor(companyTimeout)) * 60 * 1000;
                inactivity.start({ timeoutMs });
            } else {
                inactivity.stop();
            }
        },

        logout(type?: string) {
            inactivity.stop();
            this.user = undefined;
            this.company = undefined;

            if (type !== undefined && type !== null) {
                toast.info(`Logged out of ${type}`);
                console.log(`Logged out of ${type}`);
            }

            localStorage.removeItem('token');
            localStorage.removeItem('level');
            localStorage.removeItem('locale');

            // Clear inactivity keys (if present)
            localStorage.removeItem('idle.lastActivity');
            localStorage.removeItem('idle.logout');
            router.push('/login').then();
        },
    },
});
